import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { homeCareAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import {
  Home,
  MapPin,
  Clock,
  Loader2,
  CheckCircle,
  AlertCircle,
  Thermometer,
  Heart,
  Syringe,
  Baby,
  Activity,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';

const careTypes = [
  { id: 'nursing', name: 'Soins infirmiers', icon: Thermometer, description: 'Injections, pansements, prélèvements' },
  { id: 'physiotherapy', name: 'Kinésithérapie', icon: Activity, description: 'Rééducation, massage thérapeutique' },
  { id: 'elderly', name: 'Soins personnes âgées', icon: Heart, description: 'Assistance quotidienne' },
  { id: 'maternal', name: 'Soins maternels', icon: Baby, description: 'Suivi grossesse, post-accouchement' },
  { id: 'vaccination', name: 'Vaccination', icon: Syringe, description: 'Vaccins à domicile' },
];

const HomeCare = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    care_type: '',
    description: '',
    address: '',
    phone: '',
    latitude: null,
    longitude: null,
    preferred_date: '',
    preferred_time: '',
  });

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const response = await homeCareAPI.getAll();
      setRequests(response.data.requests);
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }));
          toast.success('Localisation récupérée');
        },
        () => {
          toast.error('Impossible de récupérer la localisation');
        }
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.latitude || !formData.longitude) {
      toast.error('Veuillez activer la géolocalisation');
      return;
    }

    setSubmitting(true);
    try {
      await homeCareAPI.create(formData);
      setSuccess(true);
      toast.success('Demande envoyée avec succès');
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'accepted': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-emerald-100 text-emerald-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'accepted': return 'Accepté';
      case 'completed': return 'Terminé';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="section-padding py-6">
        <div className="max-w-md mx-auto text-center py-12">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Demande envoyée!
          </h1>
          <p className="text-slate-600 mb-6">
            Votre demande de soins à domicile a été envoyée. Un professionnel vous contactera bientôt.
          </p>
          <Button
            onClick={() => { setSuccess(false); setShowForm(false); }}
            className="bg-red-500 hover:bg-red-600 text-white rounded-full"
          >
            Voir mes demandes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="section-padding py-6" data-testid="home-care-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Soins à domicile
          </h1>
          <p className="text-slate-600 mt-1">Un professionnel se déplace chez vous</p>
        </div>
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="bg-red-500 hover:bg-red-600 text-white rounded-full"
            data-testid="new-request-button"
          >
            Nouvelle demande
          </Button>
        )}
      </div>

      {showForm ? (
        <Card className="border-0 shadow-xl mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-amber-600" />
              Nouvelle demande de soins
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label>Type de soins *</Label>
                <div className="grid md:grid-cols-2 gap-3 mt-3">
                  {careTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, care_type: type.name })}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          formData.care_type === type.name
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-slate-200 hover:border-amber-200'
                        }`}
                        data-testid={`care-type-${type.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-6 h-6 text-amber-600" />
                          <div>
                            <p className="font-semibold text-slate-900">{type.name}</p>
                            <p className="text-sm text-slate-500">{type.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description du besoin *</Label>
                <Textarea
                  id="description"
                  placeholder="Décrivez vos besoins en détail..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-2"
                  rows={4}
                  required
                  data-testid="description-input"
                />
              </div>

              <div>
                <Label htmlFor="address">Adresse *</Label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(value) => setFormData({ ...formData, address: value })}
                  onSelect={(location) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      address: location.display_name,
                      latitude: location.lat,
                      longitude: location.lon
                    }));
                  }}
                  placeholder="Rechercher votre adresse ou cliquez sur la carte..."
                  showMap={true}
                  mapHeight="200px"
                  className="mt-2"
                  data-testid="address-input"
                />
                {formData.latitude && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Localisation enregistrée ({formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)})
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="phone">Numéro de téléphone *</Label>
                <div className="relative mt-2">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+237 6XX XXX XXX"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="h-12 pl-11"
                    required
                    data-testid="phone-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Le professionnel vous contactera sur ce numéro
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="preferred_date">Date préférée</Label>
                  <Input
                    id="preferred_date"
                    type="date"
                    value={formData.preferred_date}
                    onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                    className="mt-2 h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="preferred_time">Heure préférée</Label>
                  <Select
                    value={formData.preferred_time}
                    onValueChange={(value) => setFormData({ ...formData, preferred_time: value })}
                  >
                    <SelectTrigger className="mt-2 h-12">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Matin (8h-12h)</SelectItem>
                      <SelectItem value="afternoon">Après-midi (14h-18h)</SelectItem>
                      <SelectItem value="evening">Soir (18h-20h)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="flex-1 h-12 rounded-full"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !formData.care_type || !formData.description || !formData.address || !formData.phone}
                  className="flex-1 h-12 bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                  data-testid="submit-request-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>Envoyer la demande</>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      {/* Requests List */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Mes demandes</h2>
        {requests.length > 0 ? (
          <div className="space-y-3">
            {requests.map((request) => (
              <Card key={request.id} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Home className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{request.care_type}</h3>
                        <p className="text-sm text-slate-500 line-clamp-1">{request.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {request.address}
                          </span>
                        </div>
                        {request.professional_name && (
                          <p className="text-sm text-red-500 mt-2">
                            Assigné à: {request.professional_name}
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge className={getStatusColor(request.status)}>
                      {getStatusLabel(request.status)}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8 text-center">
              <Home className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucune demande de soins</p>
              <Button
                onClick={() => setShowForm(true)}
                className="mt-4 bg-amber-500 hover:bg-amber-600 text-white rounded-full"
              >
                Faire une demande
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default HomeCare;
